<template>
  <div class="atendimento-360">
    <div
      v-if="loading"
      class="atendimento-loading"
      role="status"
      aria-live="polite"
    >
      <b-spinner
        label="Autenticando"
        variant="primary"
        class="atendimento-loading__spinner"
      />

      <h4>Carregando</h4>

      <p>Aguarde, estamos carregando os dados...</p>
    </div>

    <main v-else class="atendimento-360__container">
      <section class="painel atendimento">
        <header class="atendimento__header">
          <div class="atendimento__topo">
            <h1>
              <i class="icofont-user-alt-7"></i> Atendimento multidimensional
            </h1>

            <div class="atendimento__acoes">
              <div
                class="status-switch"
                role="group"
                aria-label="Filtrar acompanhamentos"
              >
                <button
                  type="button"
                  class="status-switch__completo"
                  :class="{ active: filtroAtendimento === 'completo' }"
                  @click="selecionarFiltroAtendimento('completo')"
                >
                  <i class="icofont-check-circled"></i> Completo
                </button>
                <button
                  type="button"
                  class="status-switch__parcial"
                  :class="{ active: filtroAtendimento === 'parcial' }"
                  @click="selecionarFiltroAtendimento('parcial')"
                >
                  <i class="icofont-warning-alt"></i> Parcial
                </button>
              </div>

              <a
                href="https://measus.com.br/guia-codigos-indicadores"
                target="_blank"
                rel="noopener noreferrer"
                class="atendimento__guia"
              >
                <i class="icofont-book-alt"></i>
                Guia
              </a>

              <modal-informacao
                :isIframe="true"
                urlIframe="https://doc.clickup.com/9011510980/d/h/8cj1fp4-1711/5ac7a646cc30b1b/8cj1fp4-2511"
                pageIdentifier="pep-atendimento"
              ></modal-informacao>
            </div>
          </div>

          <div class="pessoa-resumo">
            <div class="pessoa-resumo__cabecalho">
              <div class="pessoa-resumo__nome">
                {{ pessoa.nome ?? '' }}
                <span>| {{ pessoa.sexo ?? '' }}</span>
              </div>
            </div>

            <div class="pessoa-resumo__informacoes">
              <div class="pessoa-resumo__coluna">
                <div class="pessoa-resumo__campo">
                  <span class="pessoa-resumo__rotulo">CPF</span>
                  <span class="pessoa-resumo__valor">
                    {{ formatCPF(pessoa.cpf) ?? '' }}
                  </span>

                  <template v-if="pessoa.cns">
                    <span class="pessoa-resumo__rotulo">CNS</span>
                    <span class="pessoa-resumo__valor">
                      {{ pessoa.cns ?? '' }}
                    </span>
                  </template>
                </div>

                <div class="pessoa-resumo__campo">
                  <span class="pessoa-resumo__rotulo">
                    Data de nascimento
                  </span>

                  <span class="pessoa-resumo__valor">
                    {{ pessoa.nascimento ?? '' }}

                    <template v-if="pessoa.idade">
                      ({{ pessoa.idade }} anos)
                    </template>
                  </span>
                </div>
              </div>

              <div class="pessoa-resumo__coluna">
                <div class="pessoa-resumo__campo">
                  <span class="pessoa-resumo__rotulo"> Nome da mãe </span>

                  <span class="pessoa-resumo__valor">
                    {{ pessoa.nomeMae || '' }}
                  </span>
                </div>

                <div class="pessoa-resumo__campo">
                  <span class="pessoa-resumo__rotulo"> Microárea </span>

                  <span class="pessoa-resumo__valor">
                    {{ pessoa.microArea || '' }}

                    <template v-if="pessoa.profissional">
                      - {{ pessoa.profissional }}
                    </template>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div class="cards-atendimento">
          <button
            v-for="item in acompanhamentosFiltrados"
            :key="item.id"
            type="button"
            class="card-atendimento"
            :class="[
              `card-atendimento--${item.status}`,
              { 'card-atendimento--ativo': acompanhamentoAtivoId === item.id },
            ]"
            @click="acompanhamentoAtivoId = item.id"
          >
            <i :class="item.icone"></i>
            <span>{{ item.titulo }}</span>
            <span
              class="card-atendimento__status"
              :class="`card-atendimento__status--${item.status}`"
            >
              <i
                :class="
                  item.status === 'completo'
                    ? 'icofont-check'
                    : 'icofont-warning-alt'
                "
              ></i>
            </span>
            <span
              v-if="acompanhamentoAtivoId === item.id"
              class="card-atendimento__seta"
            ></span>
          </button>
        </div>

        <div class="recomendacoes">
          <h2>NESTE ATENDIMENTO</h2>

          <div
            v-if="!acompanhamentosFiltrados.length"
            class="recomendacoes__vazio"
          >
            <template
              v-if="
                filtroAtendimento === 'completo' && possuiIndicadoresParciais
              "
            >
              <i class="icofont-warning-alt"></i>

              Acesse o filtro <strong>Parcial</strong> acima para ver o que
              precisa ser feito neste atendimento.
            </template>

            <template v-else>
              <i class="icofont-info-circle"></i>

              Nenhum indicador
              {{
                filtroAtendimento === 'completo'
                  ? 'completamente'
                  : 'parcialmente'
              }}
              acompanhado encontrado.
            </template>
          </div>

          <div
            v-else-if="todosIndicadoresCompletos"
            class="recomendacoes__sucesso"
          >
            <i class="icofont-check-circled"></i>
            Nenhuma pendência. Todos os cuidados previstos para esta pessoa
            estão em dia.
          </div>

          <div
            v-else-if="
              acompanhamentoAtivo && acompanhamentoAtivo.status === 'completo'
            "
            class="recomendacoes__mensagens"
          >
            <div class="recomendacoes__sucesso mb-3">
              <i class="icofont-check-circled"></i>

              Nenhuma pendência no indicador selecionado para esta pessoa.
            </div>

            <div class="recomendacoes__sucesso recomendacoes__sucesso--alerta">
              <i class="icofont-warning-alt"></i>

              Acesse o filtro <strong>Parcial</strong> acima para ver o que
              precisa ser feito neste atendimento.
            </div>
          </div>

          <div v-else class="recomendacoes__lista">
            <article
              v-for="(alerta, alertaIndex) in acompanhamentoAtivo.alertas"
              :key="`${acompanhamentoAtivo.id}-${alertaIndex}`"
              class="alerta"
            >
              <div class="alerta__cabecalho">
                <div class="alerta__texto">
                  <i class="icofont-warning-alt"></i>
                  <span>{{ alerta.texto }}</span>
                </div>

                <div
                  v-if="alerta.codigos && !perfilOdontoOuEmulti"
                  class="alerta__acoes"
                >
                  <button
                    v-for="(lista, tipo) in alerta.codigos"
                    :key="tipo"
                    type="button"
                    @click="abrirModalCodigos(alerta.chave)"
                  >
                    {{ rotuloTipoCodigo(tipo) }}
                  </button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <TabelaIndicadores
        :registros="registrosIndicadores"
        @abrir-sigtap="abrirModalSigtap"
        @atualizar-indicadores="indicadores = $event"
      />
    </main>

    <ModalCodigosClinicos ref="modalCodigosClinicos" />

    <ModalSigtap
      ref="modalSigtap"
      :title="modalSigtap.title"
      :numerador-label="modalSigtap.numeradorLabel"
      :denominador-label="modalSigtap.denominadorLabel"
      :procedimentos-numerador="modalSigtap.procedimentosNumerador"
      :procedimentos-denominador="modalSigtap.procedimentosDenominador"
    />
  </div>
</template>

<script>
import { jwtDecode } from 'jwt-decode'
import utils from '@/mixins/utils'
import { indicadoresConfig } from './constants/index.js'
import { rawCiap } from '@/pages/GuiaCodigosIndicadores/data/rawCiap.js'
import { rawCid } from '@/pages/GuiaCodigosIndicadores/data/rawCid.js'
import { rawSigtap } from '@/pages/GuiaCodigosIndicadores/data/rawSigtap.js'
import { getIndicadorSigtapConfig } from './config/indicadoresSigtap.js'
import ModalSigtap from './components/ModalSigtap.vue'
import TabelaIndicadores from './components/TabelaIndicadores.vue'
import ModalCodigosClinicos from './components/ModalCodigosClinicos.vue'
import ModalInformacao from '@/components/ModalInformacao.vue'

export default {
  name: 'Atendimento360',
  components: {
    ModalSigtap,
    TabelaIndicadores,
    ModalCodigosClinicos,
    ModalInformacao,
  },
  mixins: [utils],
  data() {
    return {
      loading: true,
      filtroAtendimento: 'parcial',
      acompanhamentoAtivoId: 'saude-mulher',
      buscaCodigo: '',
      codigoCopiado: '',
      codigoAberto: {
        alertaIndex: null,
        tipo: null,
      },
      pessoa: {
        cpf: '',
        cns: '',
        nome: '',
        sexo: '',
        nascimento: '',
        idade: '',
        nomeMae: '',
        profissional: '',
        microArea: '',
        atualizadoEm: '',
        indicadores: {},
        buscaAtiva: {},
        dadosOriginais: {},
      },
      acompanhamentosConfig: {
        b1: {
          id: 'primeira-consulta',
          icone: 'icofont-stethoscope',
        },
        b2: {
          id: 'odontologico',
          icone: 'icofont-tooth',
        },
        b3: {
          id: 'exodontias',
          icone: 'icofont-tooth',
        },
        b4: {
          id: 'escovacao',
          icone: 'icofont-tooth',
        },
        b5: {
          id: 'procedimentos-preventivos',
          icone: 'icofont-tooth',
        },
        b6: {
          id: 'tratamento-atraumatico',
          icone: 'icofont-tooth',
        },
        b7: {
          id: 'gestantes-odontologia',
          icone: 'icofont-businesswoman',
        },
        c1: {
          id: 'mais-acesso',
          icone: 'icofont-doctor',
        },
        c2: {
          id: 'desenvolvimento-infantil',
          icone: 'icofont-baby',
        },
        c3: {
          id: 'gestantes',
          icone: 'icofont-businesswoman',
        },
        c4: {
          id: 'diabetes',
          icone: 'icofont-blood-drop',
        },
        c5: {
          id: 'hipertensao',
          icone: 'icofont-heart-beat',
        },
        c6: {
          id: 'idosa',
          icone: 'icofont-users-alt-5',
        },
        c7: {
          id: 'saude-mulher',
          icone: 'icofont-businesswoman',
        },
      },
      codigosBuscaAtiva: {
        c3a: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: [
            'HIV',
            'SIFILIS',
            'HEPATITEB',
            'HEPATITEA',
            'GRAVIDEZ',
            'PARTO',
          ],
        },
        c3b: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: [
            'HIV',
            'SIFILIS',
            'HEPATITEB',
            'HEPATITEA',
            'GRAVIDEZ',
            'PARTO',
          ],
        },
        c3g: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: ['HIV', 'SIFILIS'],
        },
        c3h: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: ['HIV', 'SIFILIS'],
        },
        c3i: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: [
            'HIV',
            'SIFILIS',
            'HEPATITEB',
            'HEPATITEA',
            'GRAVIDEZ',
            'PARTO',
          ],
        },
        c3k: {
          ciap: [],
          cid: [],
          sigtap: [],
        },
        c4a: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: [],
        },
        c4b: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: ['PA'],
        },
        c4d: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: ['ANTROP', 'PESO', 'ALTURA'],
        },
        c4e: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: ['DIABETES'],
        },
        c4f: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: ['DIABETES'],
        },
        c5a: {
          ciap: ['HIPERTENSAO'],
          cid: ['HIPERTENSAO'],
          sigtap: [],
        },
        c5b: {
          ciap: ['HIPERTENSAO'],
          cid: ['HIPERTENSAO'],
          sigtap: ['PA'],
        },
        c5d: {
          ciap: ['HIPERTENSAO'],
          cid: ['HIPERTENSAO'],
          sigtap: ['ANTROP', 'PESO', 'ALTURA'],
        },
        c6b: {
          ciap: [],
          cid: [],
          sigtap: ['ANTROP', 'PESO', 'ALTURA'],
        },
        c7a: {
          ciap: ['SAUDE_SEXUAL_REPRODUTIVA'],
          cid: ['SAUDE_SEXUAL_REPRODUTIVA'],
          sigtap: ['UTERO'],
        },
        c7c: {
          ciap: ['SAUDE_SEXUAL_REPRODUTIVA'],
          cid: ['SAUDE_SEXUAL_REPRODUTIVA'],
          sigtap: ['UTERO', 'MAMA'],
        },
        c7d: {
          ciap: ['SAUDE_SEXUAL_REPRODUTIVA'],
          cid: ['SAUDE_SEXUAL_REPRODUTIVA'],
          sigtap: ['MAMA'],
        },
      },
      indicadores: [],
      registrosIndicadores: [],
      dataFonteIndicadores: '',
      modalSigtap: {
        title: 'SIGTAP',
        numeradorLabel: 'Numerador',
        denominadorLabel: 'Denominador',
        procedimentosNumerador: [],
        procedimentosDenominador: [],
      },
      filtrosGuiaCodigos: {
        c2c: ['PESO', 'ALTURA', 'ANTROPOMÉTRICA'],
        c2e: ['PENTAVALENTE', 'POLIOMIELITE', 'TRÍPLICE VIRAL'],

        c3a: ['PRÉ-NATAL', 'GRAVIDEZ'],
        c3b: ['PRÉ-NATAL', 'GRAVIDEZ'],
        c3c: ['PRESSÃO ARTERIAL'],
        c3d: ['PESO', 'ALTURA', 'ANTROPOMÉTRICA'],
        c3g: ['SÍFILIS', 'HIV', 'HEPATITE'],
        c3h: ['SÍFILIS', 'HIV'],
        c3i: ['PUERPÉRIO', 'PUERPERAL'],

        c4a: ['DIABETES'],
        c4b: ['PRESSÃO ARTERIAL'],
        c4d: ['PESO', 'ALTURA', 'ANTROPOMÉTRICA'],
        c4e: ['HEMOGLOBINA GLICOSILADA'],
        c4f: ['PÉ DIABÉTICO'],

        c5a: ['HIPERTENSÃO'],
        c5b: ['PRESSÃO ARTERIAL'],
        c5d: ['PESO', 'ALTURA', 'ANTROPOMÉTRICA'],

        c6b: ['PESO', 'ALTURA', 'ANTROPOMÉTRICA'],

        c7a: ['ACONSELHAMENTO', 'CITOPATOLÓGICO', 'ÚTERO'],
        c7b: ['ACONSELHAMENTO', 'HPV'],
        c7c: ['ACONSELHAMENTO', 'SEXUAL', 'REPRODUTIVA'],
        c7d: ['ACONSELHAMENTO', 'MAMA', 'MAMOGRAFIA'],
      },
    }
  },
  async mounted() {
    await this.carregarDadosAtendimento()
  },
  computed: {
    acompanhamentosFiltrados() {
      return this.acompanhamentos.filter(
        (item) => item.status === this.filtroAtendimento
      )
    },

    acompanhamentoAtivo() {
      return (
        this.acompanhamentos.find(
          (item) => item.id === this.acompanhamentoAtivoId
        ) ||
        this.acompanhamentosFiltrados[0] ||
        null
      )
    },

    /**
     * Indica se o profissional pertence a uma equipe de saúde bucal
     * ou eMulti, conforme os indicadores retornados na série histórica.
     *
     * @returns {boolean}
     */
    perfilOdontoOuEmulti() {
      return this.registrosIndicadores.some((registro) =>
        [40, 44].includes(Number(registro?.indicador))
      )
    },

    /**
     * Indica se o profissional pertence a uma equipe de saúde da família
     * ou eMulti, conforme os indicadores retornados na série histórica.
     *
     * @returns {boolean}
     */
    perfilEsfOuEmulti() {
      return this.registrosIndicadores.some((registro) =>
        [49, 44].includes(Number(registro?.indicador))
      )
    },

    /**
     * Indica se o profissional pertence a uma equipe de saúde bucal.
     *
     * @returns {boolean}
     */
    perfilOdontologo() {
      return this.registrosIndicadores.some(
        (registro) => Number(registro?.indicador) === 40
      )
    },

    /**
     * Monta os acompanhamentos do cidadão utilizando as mesmas
     * regras do componente MaisIndicadores.
     *
     * @returns {Array}
     */
    acompanhamentos() {
      const indicadores = this.pessoa.indicadores || {}
      const buscaAtiva = this.pessoa.buscaAtiva || {}

      return Object.keys(indicadores)
        .sort()
        .map((indicadorKey) => {
          const regraIndicador = indicadoresConfig[indicadorKey]
          const visual = this.acompanhamentosConfig[indicadorKey]

          if (!regraIndicador || !visual) {
            return null
          }

          const alertas = this.montarAlertasIndicador(
            indicadorKey,
            regraIndicador,
            buscaAtiva
          )

          const classificacao = this.getClassificacaoIndicador(indicadorKey)

          return {
            id: visual.id,
            indicador: indicadorKey,
            titulo: regraIndicador.label,
            icone: visual.icone,
            classificacao,
            cor: this.getCorIndicador(classificacao),
            percentual: Number(indicadores[indicadorKey] || 0),
            status: alertas.length > 0 ? 'parcial' : 'completo',
            alertas,
          }
        })
        .filter(Boolean)
    },

    fontesCodigosClinicos() {
      return {
        ciap: rawCiap,
        cid: rawCid,
        sigtap: rawSigtap,
      }
    },

    possuiIndicadoresParciais() {
      return this.acompanhamentos.some((item) => item.status === 'parcial')
    },

    todosIndicadoresCompletos() {
      return (
        this.acompanhamentos.length > 0 &&
        this.acompanhamentos.every((item) => item.status === 'completo')
      )
    },
  },
  methods: {
    /**
     * Retorna a classificação do indicador.
     *
     * @param {string} indicadorKey
     * @returns {string}
     */
    getClassificacaoIndicador(indicadorKey) {
      const mapa = {
        c1: 46,
        c2: 51,
        c3: 52,
        c4: 48,
        c5: 50,
        c6: 49,
        c7: 47,
        b1: 38,
        b2: 43,
        b3: 39,
        b4: 42,
        b5: 40,
        b6: 41,
        m1: 44,
        m2: 45,
      }

      const indicador = this.indicadores.find(
        (item) => item.id === mapa[indicadorKey]
      )

      return indicador?.classificacao || 'Regular'
    },
    /**
     * Retorna a cor visual do acompanhamento conforme
     * a classificação do indicador.
     *
     * @param {string} classificacao
     * @returns {string}
     */
    getCorIndicador(classificacao) {
      switch (this.normalizar(classificacao)) {
        case 'otimo':
          return 'otima'

        case 'bom':
          return 'boa'

        case 'suficiente':
          return 'suficiente'

        default:
          return 'regular'
      }
    },

    /**
     * Monta os alertas pendentes de um indicador.
     *
     * Uma busca ativa é considerada pendente quando seu valor é igual a zero.
     *
     * @param {string} indicadorKey
     * @param {Object} indicadorConfig
     * @param {Object} buscaAtiva
     * @returns {Array}
     */
    montarAlertasIndicador(indicadorKey, indicadorConfig, buscaAtiva) {
      const definicoes = indicadorConfig.buscaAtiva || {}

      return Object.keys(buscaAtiva)
        .filter((key) => key.startsWith(indicadorKey))
        .sort()
        .map((key) => {
          const sufixo = key.replace(indicadorKey, '')
          const definicao = definicoes[sufixo]

          return {
            chave: key,
            valor: Number(buscaAtiva[key]),
            texto: this.getTextoBuscaAtiva(definicao, key),
            codigos: this.getCodigosBuscaAtiva(key),
          }
        })
        .filter((item) => item.valor === 0)
    },

    /**
     * Retorna o texto configurado para um item de busca ativa.
     *
     * @param {string|Function|undefined} definicao
     * @param {string} key
     * @returns {string}
     */
    getTextoBuscaAtiva(definicao, key) {
      if (typeof definicao === 'function') {
        return definicao({
          cidadao: this.pessoa.dadosOriginais,
          formatDate: this.formatDate,
          perfilOdontoOuEmulti: this.perfilOdontoOuEmulti,
          perfilEsfOuEmulti: this.perfilEsfOuEmulti,
          perfilOdontologo: this.perfilOdontologo,
        })
      }

      if (typeof definicao === 'string') {
        return definicao
      }

      return `Item ${key}`
    },

    /**
     * Retorna os códigos clínicos associados a uma busca ativa,
     * filtrando as fontes pelo campo id_grupo.
     *
     * @param {string} key
     * @returns {Object|null}
     */
    getCodigosBuscaAtiva(key) {
      const configuracao = this.codigosBuscaAtiva[key]

      if (!configuracao) {
        return null
      }

      const codigos = Object.entries(configuracao).reduce(
        (resultado, [tipo, grupos]) => {
          const fonte = this.fontesCodigosClinicos[tipo] || []

          const itens = this.filtrarCodigosPorGrupo(fonte, grupos)

          if (itens.length > 0) {
            resultado[tipo] = itens
          }

          return resultado
        },
        {}
      )

      return Object.keys(codigos).length > 0 ? codigos : null
    },

    /**
     * Filtra uma lista de códigos pelos grupos configurados.
     *
     * @param {Array} fonte
     * @param {Array} grupos
     * @returns {Array}
     */
    filtrarCodigosPorGrupo(fonte, grupos) {
      if (!Array.isArray(fonte) || !Array.isArray(grupos)) {
        return []
      }

      const gruposNormalizados = grupos.map((grupo) =>
        this.normalizarGrupoCodigo(grupo)
      )

      return fonte
        .filter((item) =>
          gruposNormalizados.includes(
            this.normalizarGrupoCodigo(item?.id_grupo)
          )
        )
        .map((item) => ({
          codigo: String(item?.codigo || ''),
          descricao: String(item?.nome || item?.descricao || ''),
          grupo: String(item?.id_grupo || ''),
        }))
        .filter((item) => item.codigo)
    },

    /**
     * Normaliza o identificador de grupo utilizado pelos arquivos de códigos.
     *
     * @param {string} grupo
     * @returns {string}
     */
    normalizarGrupoCodigo(grupo) {
      return String(grupo || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .trim()
    },

    /**
     * Carrega os dados necessários para montar a página de atendimento 360.
     */
    async carregarDadosAtendimento() {
      this.loading = true

      const token = this.$store.getters['pep/token']

      if (!token) {
        console.error('Token de autenticação do PEP não encontrado.')
        return
      }

      try {
        const dadosToken = jwtDecode(token)

        const coIbge = String(dadosToken.ibge || '').trim()

        const documentoProfissional = String(
          dadosToken.documento_profissional || ''
        ).trim()

        const documentoCidadao = String(
          dadosToken.documento_cidadao || ''
        ).trim()

        if (!coIbge) {
          throw new Error('Código IBGE não encontrado no token do PEP.')
        }

        await this.buscarCidadao(coIbge, documentoCidadao)

        const profissional = await this.buscarProfissionalSaude(
          coIbge,
          documentoProfissional
        )

        const nuIne = String(profissional?.nu_ine || '').trim()

        if (!nuIne) {
          console.warn('O profissional de saúde não possui INE informado.')
          return
        }

        await this.buscarSerieHistoricaEquipe(coIbge, nuIne)
      } catch (error) {
        console.error('Erro ao carregar os dados do atendimento 360:', error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Busca os dados do cidadão informado pelo PEC.
     *
     * @param {string} coIbge
     * @param {string} documentoCidadao
     * @returns {Promise<Object>}
     */
    async buscarCidadao(coIbge, documentoCidadao) {
      const payload = this.montarPayloadDocumentoCidadao(documentoCidadao)

      const { data } = await this.$apiPep.post(
        `/pep/app/cidadao/${coIbge}`,
        payload
      )

      this.pessoa = this.mapearDadosCidadao(data)

      await this.$nextTick()

      this.selecionarPrimeiroAcompanhamento()

      return data
    },

    /**
     * Seleciona inicialmente o primeiro acompanhamento parcial.
     *
     * Caso não existam pendências, seleciona o primeiro acompanhamento completo.
     *
     * @returns {void}
     */
    selecionarPrimeiroAcompanhamento() {
      const primeiroParcial = this.acompanhamentos.find(
        (item) => item.status === 'parcial'
      )

      const primeiro = primeiroParcial || this.acompanhamentos[0] || null

      if (!primeiro) {
        this.acompanhamentoAtivoId = null
        return
      }

      this.filtroAtendimento = primeiro.status
      this.acompanhamentoAtivoId = primeiro.id
    },

    /**
     * Converte o retorno da API para a estrutura utilizada pela interface.
     *
     * @param {Object} cidadao
     * @returns {Object}
     */
    mapearDadosCidadao(cidadao) {
      return {
        cpf: String(cidadao?.nu_cpf || ''),
        cns: String(cidadao?.nu_cns || ''),
        nome: String(cidadao?.no_cidadao || ''),
        sexo: this.formatarTexto(cidadao?.sexo),
        nascimento: this.formatarData(cidadao?.dt_nascimento),
        idade: Number(cidadao?.idade || 0),
        nomeMae: String(cidadao?.no_mae || ''),
        profissional: String(cidadao?.no_profissional || ''),
        microArea: String(cidadao?.micro_area || ''),
        atualizadoEm: this.formatarDataHora(cidadao?.dt_atualizado),
        indicadores: this.parseObjectJson(cidadao?.indicadores),
        buscaAtiva: this.parseObjectJson(cidadao?.busca_ativa),
        dadosOriginais: cidadao || {},
      }
    },

    /**
     * Formata uma data no padrão AAAA-MM-DD para DD/MM/AAAA.
     *
     * @param {string} data
     * @returns {string}
     */
    formatarData(data) {
      const valor = String(data || '').trim()

      if (!valor) {
        return '-'
      }

      const apenasData = valor.split(' ')[0]
      const partes = apenasData.split('-')

      if (partes.length !== 3) {
        return valor
      }

      const [ano, mes, dia] = partes

      return `${dia}/${mes}/${ano}`
    },

    /**
     * Formata uma data e hora retornada pela API.
     *
     * @param {string} dataHora
     * @returns {string}
     */
    formatarDataHora(dataHora) {
      const valor = String(dataHora || '').trim()

      if (!valor) {
        return '-'
      }

      const [data, hora = ''] = valor.split(' ')
      const dataFormatada = this.formatarData(data)

      return hora ? `${dataFormatada} ${hora}` : dataFormatada
    },

    /**
     * Formata um texto deixando apenas a primeira letra maiúscula.
     *
     * @param {string} texto
     * @returns {string}
     */
    formatarTexto(texto) {
      const valor = String(texto || '')
        .trim()
        .toLowerCase()

      if (!valor) {
        return '-'
      }

      return valor.charAt(0).toUpperCase() + valor.slice(1)
    },

    /**
     * Busca os dados do profissional de saúde informado pelo PEC.
     *
     * @param {string} coIbge
     * @param {string} documentoProfissional
     * @returns {Promise<Object>}
     */
    async buscarProfissionalSaude(coIbge, documentoProfissional) {
      const { data } = await this.$apiPep.post(
        `/pep/app/profissional-saude/${coIbge}`,
        {
          nu_cpf: documentoProfissional,
        }
      )

      return data
    },

    /**
     * Busca a série histórica da equipe do profissional autenticado.
     *
     * @param {string} coIbge
     * @param {string} nuIne
     * @returns {Promise<Array>}
     */
    async buscarSerieHistoricaEquipe(coIbge, nuIne) {
      const { data } = await this.$apiPep.get(
        `/pep/app/serie-historica/${coIbge}/equipe/${nuIne}`
      )

      this.registrosIndicadores = Array.isArray(data) ? data : []

      return data
    },

    /**
     * Monta o corpo da busca do cidadão conforme o documento recebido.
     *
     * @param {string} documentoCidadao
     * @returns {{nu_cpf?: string, nu_cns?: string}}
     */
    montarPayloadDocumentoCidadao(documentoCidadao) {
      const documento = String(documentoCidadao || '').replace(/\D/g, '')

      if (documento.length === 11) {
        return {
          nu_cpf: documento,
        }
      }

      if (documento.length === 15) {
        return {
          nu_cns: documento,
        }
      }

      throw new Error(
        'O documento do cidadão informado pelo PEC não é um CPF ou CNS válido.'
      )
    },

    /**
     * Altera o filtro de acompanhamentos exibido e seleciona
     * automaticamente o primeiro item correspondente.
     *
     * @param {string} status
     * @returns {void}
     */
    selecionarFiltroAtendimento(status) {
      this.filtroAtendimento = status

      const primeiro = this.acompanhamentos.find(
        (item) => item.status === status
      )

      this.acompanhamentoAtivoId = primeiro ? primeiro.id : null
    },

    /**
     * Normaliza um texto para comparações e buscas,
     * removendo acentos e convertendo para minúsculas.
     *
     * @param {*} valor
     * @returns {string}
     */
    normalizar(valor) {
      return String(valor || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },

    /**
     * Preenche e abre o modal de procedimentos SIGTAP.
     *
     * Atualmente apenas os indicadores 39, 40 e 41 possuem suporte.
     *
     * @param {Object} indicador
     * @returns {void}
     */
    abrirModalSigtap(indicador) {
      const indicadoresHabilitados = [39, 40, 41]

      if (!indicadoresHabilitados.includes(Number(indicador?.id))) {
        return
      }

      const configuracao = getIndicadorSigtapConfig(indicador.id)

      if (!configuracao) {
        return
      }

      this.modalSigtap = {
        title: configuracao.title || indicador.nome || 'SIGTAP',

        numeradorLabel: configuracao.numerador?.label || 'Numerador',

        denominadorLabel: configuracao.denominador?.label || 'Denominador',

        procedimentosNumerador: Array.isArray(
          configuracao.numerador?.procedimentos
        )
          ? configuracao.numerador.procedimentos
          : [],

        procedimentosDenominador: Array.isArray(
          configuracao.denominador?.procedimentos
        )
          ? configuracao.denominador.procedimentos
          : [],
      }

      this.$nextTick(() => {
        this.$refs.modalSigtap?.open()
      })
    },

    /**
     * Retorna o rótulo de apresentação de um tipo de código clínico.
     *
     * @param {string} tipo
     * @returns {string}
     */
    rotuloTipoCodigo(tipo) {
      return (
        {
          ciap: 'CIAP-2',
          cid: 'CID-10',
          sigtap: 'SIGTAP',
        }[tipo] || String(tipo || '').toUpperCase()
      )
    },

    /**
     * Abre o modal com o guia de códigos dos indicadores.
     *
     * @param {string} chaveBuscaAtiva
     * @returns {void}
     */
    abrirModalCodigos(chaveBuscaAtiva) {
      const busca = this.filtrosGuiaCodigos[chaveBuscaAtiva] || ''

      this.$refs.modalCodigosClinicos.open(busca)
    },
  },
}
</script>

<style lang="scss" scoped>
.atendimento-360 {
  min-height: 100vh;
  padding: 2rem;
  color: #1f2937;
  background: #f8fafc;

  &__container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
  }
}

.painel {
  padding: 2rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
}

.atendimento {
  &__header {
    padding-bottom: 1.25rem;
  }

  &__topo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    h1 {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      margin: 0;
      font-size: 1.55rem;
      font-weight: 700;
      color: #111827;

      i {
        color: #3b82f6;
      }
    }
  }

  &__acoes {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__guia {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    padding: 0.5rem 0.9rem;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 700;
    text-decoration: none;
    background: #0fafd7;
    border: 1px solid #0fafd7;
    border-radius: 999px;
    transition: 0.2s;

    &:hover,
    &:focus {
      color: #fff;
      text-decoration: none;
      background: darken(#0fafd7, 8%);
      border-color: darken(#0fafd7, 8%);
    }

    i {
      font-size: 0.9rem;
    }
  }
}

.status-switch,
.segmentado {
  display: inline-flex;
  padding: 0.25rem;
  background: #e5e7eb;
  border-radius: 999px;

  button {
    min-width: 120px;
    padding: 0.5rem 0.9rem;
    color: #6b7280;
    font-size: 0.78rem;
    font-weight: 700;
    background: transparent;
    border: 0;
    border-radius: 999px;
    transition: 0.2s;

    &.active {
      background: #fff;
      box-shadow: 0 1px 4px rgba(15, 23, 42, 0.15);
    }
  }

  &__completo {
    color: #15803d;

    &:hover,
    &.active {
      color: #15803d;
    }
  }

  &__parcial {
    color: #dc2626;

    &:hover,
    &.active {
      color: #dc2626;
    }
  }
}

.pessoa-resumo {
  padding: 1rem 1.25rem;
  margin-top: 1rem;
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 10px;

  &__cabecalho {
    margin-bottom: 5px;
  }

  &__nome {
    font-size: 16px;
    font-weight: 700;
    color: #111827;

    span {
      color: #000;
      font-weight: 500;
      text-transform: uppercase;
      font-size: 14px;
    }
  }

  &__informacoes {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem 2rem;
  }

  &__coluna {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  &__campo {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
  }

  &__rotulo {
    font-size: 16px;
    font-weight: 700;
    color: #000;
  }

  &__valor {
    color: #111827;
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .pessoa-resumo {
    &__informacoes {
      grid-template-columns: 1fr;
    }
  }
}

.cards-atendimento {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  margin-bottom: 2rem;
}

.card-atendimento {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 1rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 2px solid #dbeafe;
  border-radius: 0.75rem;
  transition: 0.2s;

  > i {
    font-size: 1.25rem;
  }
  &:hover,
  &--ativo {
    background: #f8fafc;
    box-shadow: 0 2px 7px rgba(15, 23, 42, 0.08);
  }

  &--otima {
    border-color: #1212b6;

    > i {
      color: #1212b6;
    }

    .card-atendimento__seta {
      border-top-color: #1212b6;
    }
  }

  &--boa {
    border-color: #38ba38;

    > i {
      color: #38ba38;
    }

    .card-atendimento__seta {
      border-top-color: #38ba38;
    }
  }

  &--suficiente {
    border-color: #d5ba0a;

    > i {
      color: #d5ba0a;
    }

    .card-atendimento__seta {
      border-top-color: #d5ba0a;
    }
  }

  &--regular {
    border-color: #c46500;

    > i {
      color: #c46500;
    }

    .card-atendimento__seta {
      border-top-color: #c46500;
    }
  }

  &--completo {
    border-color: #16a34a;

    > i {
      color: #16a34a;
    }

    .card-atendimento__seta {
      border-top-color: #16a34a;
    }
  }

  &--parcial {
    border-color: #dc2626;

    > i {
      color: #dc2626;
    }

    .card-atendimento__seta {
      border-top-color: #dc2626;
    }
  }

  &__status {
    position: absolute;
    top: -0.45rem;
    right: -0.45rem;
    display: grid;
    width: 1.35rem;
    height: 1.35rem;
    place-items: center;
    font-size: 0.7rem;
    border: 2px solid #fff;
    border-radius: 50%;

    &--parcial {
      color: #dc2626;
      background: #fee2e2;
    }
    &--completo {
      color: #16a34a;
      background: #dcfce7;
    }
  }

  &__seta {
    position: absolute;
    bottom: -0.55rem;
    left: 50%;
    width: 0;
    height: 0;
    border-top: 0.55rem solid #cbd5e1;
    border-right: 0.55rem solid transparent;
    border-left: 0.55rem solid transparent;
    transform: translateX(-50%);
  }
}

.recomendacoes {
  padding: 1.25rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.8rem;

  h2 {
    margin: 0 0 1rem;
    font-size: 0.78rem;
    font-weight: 800;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  &__lista {
    display: grid;
    gap: 1rem;
  }

  &__sucesso {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1.25rem;
    color: #15803d;
    font-size: 0.9rem;
    font-weight: 600;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 0.75rem;
    &--alerta {
      color: #991b1b;
      background: #fef2f2;
      border-color: #fecaca;

      i {
        color: #dc2626;
      }
    }
  }

  &__vazio {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1.25rem;
    color: #64748b;
    font-size: 0.9rem;
    font-weight: 600;
    text-align: center;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 0.75rem;

    i {
      color: #94a3b8;
      font-size: 1rem;
    }
  }
}

.alerta {
  overflow: hidden;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;

  &__cabecalho {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
  }

  &__texto {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    color: #991b1b;
    font-size: 0.86rem;
    font-weight: 600;
    line-height: 1.45;

    i {
      margin-top: 0.15rem;
      color: #dc2626;
      font-size: 1.15rem;
    }
  }

  &__acoes {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;

    button {
      padding: 0.4rem 0.65rem;
      color: #4b5563;
      font-size: 0.72rem;
      font-weight: 700;
      background: #fff;
      border: 1px solid #d1d5db;
      border-radius: 0.4rem;

      &.active {
        color: #1d4ed8;
        background: #eff6ff;
        border-color: #60a5fa;
      }
    }
  }
}

.atendimento-loading {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 460px;
  padding: 2rem;
  text-align: center;
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.06);
  margin: 50px auto;

  h4 {
    color: #1f2937;
    font-weight: 700;
  }

  p {
    max-width: 360px;
    margin: 0;
    color: #6c757d;
    line-height: 1.5;
  }

  &__spinner {
    width: 3rem;
    height: 3rem;
  }
}

@keyframes atendimento-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .atendimento-360 {
    padding: 1rem;
  }

  .painel {
    padding: 1rem;
  }

  .atendimento__topo,
  .alerta__cabecalho {
    align-items: stretch;
    flex-direction: column;
  }

  .status-switch {
    width: 100%;
  }

  .status-switch button {
    flex: 1;
    min-width: 0;
  }

  .card-atendimento {
    width: 100%;
  }
}
</style>
